import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import StaffListModal from "../../components/company-settings/StaffListModal";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const RoleViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const roleId = useMemo(
    () => location.state?.roleId || sessionStorage.getItem("selectedRoleId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_role_details_${roleId || "unknown"}`, [roleId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { role: null }), [cacheKey]);

  const [role, setRole] = useState(cachedData?.role || null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [isLoading, setIsLoading] = useState(!cachedData?.role);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [staffErrorMessage, setStaffErrorMessage] = useState("");

  const resolveStaffList = (response) => {
    const payload = response?.data || {};

    if (Array.isArray(payload.staff)) return payload.staff;
    if (Array.isArray(payload.employees)) return payload.employees;
    if (Array.isArray(payload.data)) return payload.data;

    return [];
  };

  const loadRoleStaff = useCallback(async () => {
    if (!apiBaseUrl || !roleId) {
      setStaffErrorMessage("Unable to load staff for this role right now.");
      return;
    }

    try {
      setIsStaffLoading(true);
      setStaffErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/roles/${roleId}/staff`);
      setStaffMembers(resolveStaffList(response));
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load staff for this role. Please try again.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      setStaffMembers([]);
    } finally {
      setIsStaffLoading(false);
    }
  }, [apiBaseUrl, roleId]);

  const openStaffModal = () => {
    setIsStaffModalOpen(true);
    void loadRoleStaff();
  };

  const loadRole = useCallback(async ({ silent = false } = {}) => {
      if (!apiBaseUrl || !roleId) {
        setErrorMessage(!roleId ? "Select a role from the list first." : "API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/roles/${roleId}`);
        const loadedRole = response?.data?.role || null;

        setRole(loadedRole);
        setRoleName(loadedRole?.roleName || "");
        setRoleDescription(loadedRole?.roleDescription || "");
        setSelectedDepartmentId(loadedRole?.departmentId || "");
        writeSessionCache(cacheKey, { role: loadedRole });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load role details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    }, [apiBaseUrl, cacheKey, roleId]);

  useEffect(() => {
    loadRole({ silent: true });
  }, [loadRole]);

  useEffect(() => {
    const loadDepartments = async () => {
      if (!apiBaseUrl) {
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/organizations/departments`);
        setDepartments(response?.data?.departments || []);
      } catch (_error) {
        setDepartments([]);
      }
    };

    loadDepartments();
  }, [apiBaseUrl]);

  useEffect(() => {
    const loadDepartments = async () => {
      if (!apiBaseUrl) {
        return;
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/organizations/departments`);
        setDepartments(response?.data?.departments || []);
      } catch (_error) {
        setDepartments([]);
      }
    };

    loadDepartments();
  }, [apiBaseUrl]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadRole({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadRole]);

  const refreshRole = async () => {
    if (!apiBaseUrl || !roleId) {
      return;
    }

    const response = await axios.get(`${apiBaseUrl}/organizations/roles/${roleId}`);
    const loadedRole = response?.data?.role || null;

    setRole(loadedRole);
    setRoleName(loadedRole?.roleName || "");
    setRoleDescription(loadedRole?.roleDescription || "");
    setSelectedDepartmentId(loadedRole?.departmentId || "");
  };

  const handleOpenEditModal = () => {
    setFormErrorMessage("");
    setOpenEditModal(true);
  };

  const openDeleteRole = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const handleUpdateRole = async (ev) => {
    ev.preventDefault();

    if (!roleId || !apiBaseUrl) {
      setFormErrorMessage("Unable to update this role right now.");
      return;
    }

    if (!roleName.trim() || !roleDescription.trim()) {
      setFormErrorMessage("Role name and role description are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/roles/${roleId}`, {
        roleName: roleName.trim(),
        roleDescription: roleDescription.trim(),
        departmentId: selectedDepartmentId || undefined,
      });

      await refreshRole();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update role. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleId || !apiBaseUrl) {
      setDeleteErrorMessage("Unable to delete this role right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/roles/${roleId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedRoleId");

      setTimeout(() => {
        navigate("/company-settings/role");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete role. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = role?.roleName || "Role";
  const descriptionText =
    role?.roleDescription || "No role description available yet.";
  const createdAt = role?.createdAt
    ? new Date(role.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Roles", path: "/company-settings/role" },
          { label: title },
        ]}
        title={title}
        subtitle="Role details and management actions."
        fallbackPath="/company-settings/role"
        backLabel="Back to Roles"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenEditModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeleteRole}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !role ? (
        <FetchLoadingState message="Loading role details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !role ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && role ? (
        <InfoCard
          details="Role Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Department", value: role?.departmentName || "-" },
            { label: "Total staff", value: role?.totalStaff ?? 0 },
            { label: "Role ID", value: role?.id || "-" },
          ]}
        />
      ) : null}

      {!isLoading && role ? (
        <div className="px-6 mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openStaffModal}
            className="px-4 py-2 rounded-xl bg-[#1234CD] text-white font-medium"
          >
            View Role Staff
          </button>
        </div>
      ) : null}

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={closeDeleteModal}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete role"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this role?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={closeDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeleteRole}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="role deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Role deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The role was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">Edit Role</p>
          </div>
          <form
            className="my-12"
            onSubmit={handleUpdateRole}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="role-department"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Department
              </label>
              <select
                id="role-department"
                value={selectedDepartmentId}
                onChange={(ev) => setSelectedDepartmentId(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              >
                <option value="">Select department</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.departmentName || department.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="role-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Role Name
              </label>
              <input
                type="text"
                id="role-name"
                value={roleName}
                onChange={(ev) => setRoleName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="role-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Role Description
              </label>
              <textarea
                id="role-description"
                value={roleDescription}
                onChange={(ev) => setRoleDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex justify-center gap-12 mt-20 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      <StaffListModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        title={`${title} Staff`}
        subtitle="Staff assigned to this role."
        staff={staffMembers}
        loading={isStaffLoading}
        error={staffErrorMessage}
        onRefresh={loadRoleStaff}
      />
    </div>
  );
};

export default RoleViewDetails;
